<div class="flex flex-col max-h-[85vh] w-full max-w-2xl sm:max-w-3xl overflow-hidden bg-white rounded-2xl shadow-xl">

    <!-- Fixed Header -->
    <div class="p-6 pb-4 border-b border-slate-100 shrink-0 bg-white">
        <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                    <h2 class="text-xl font-bold text-slate-900">License Plates</h2>
                    <span class="bg-blue-50 text-blue-700 text-xs px-2.5 py-0.5 rounded-full font-semibold">
                        {{ plates().length }} {{ plates().length === 1 ? 'Plate' : 'Plates' }}
                    </span>
                    @if (isGuest) {
                    <span class="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">Guest Parker</span>
                    } @else {
                    <span class="bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">Registered Parker</span>
                    }
                </div>
                <p class="text-sm text-slate-500 mt-1 truncate">{{ data.displayName }}</p>
            </div>
            <button type="button" (click)="close()" class="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors" title="Close dialog">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    </div>

    <!-- Scrollable Content Body -->
    <div class="px-6 py-5 overflow-y-auto flex-1 space-y-4">

        <!-- Navigation Tabs / Modes -->
        <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
        <button type="button" (click)="setViewMode('list')"
            [class]="viewMode() === 'list' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-600 hover:text-slate-900 font-medium'"
            class="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs transition-all shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            Plates List ({{ plates().length }})
        </button>
        <button type="button" (click)="setViewMode('bulk-add')"
            [class]="viewMode() === 'bulk-add' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-600 hover:text-slate-900 font-medium'"
            class="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs transition-all shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Bulk Add / Import
        </button>
        <button type="button" (click)="setViewMode('single-add')"
            [class]="viewMode() === 'single-add' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-600 hover:text-slate-900 font-medium'"
            class="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs transition-all shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Single Plate
        </button>
    </div>

    <!-- Alert Messages -->
    @if (errorMessage()) {
    <div class="mb-4">
        <jjames-alert type="error" [dismissible]="true" (dismissed)="errorMessage.set(null)">{{ errorMessage() }}</jjames-alert>
    </div>
    }
    @if (successMessage()) {
    <div class="mb-4">
        <jjames-alert type="success" [dismissible]="true" (dismissed)="successMessage.set(null)">{{ successMessage() }}</jjames-alert>
    </div>
    }

    <!-- Batch Progress Bar -->
    @if (bulkProcessing() && bulkProgress()) {
    <div class="mb-5 rounded-xl border border-blue-200 bg-blue-50/70 p-4">
        <div class="flex items-center justify-between text-xs font-semibold text-blue-900 mb-1.5">
            <span>Processing batch...</span>
            <span>{{ bulkProgress()!.current }} / {{ bulkProgress()!.total }} ({{ (bulkProgress()!.current / bulkProgress()!.total * 100) | number:'1.0-0' }}%)</span>
        </div>
        <div class="w-full bg-blue-200 h-2 rounded-full overflow-hidden">
            <div class="bg-blue-600 h-full rounded-full transition-all duration-300"
                [style.width.%]="(bulkProgress()!.current / bulkProgress()!.total * 100)"></div>
        </div>
        <p class="text-xs text-blue-700 mt-2 truncate font-mono">Current: {{ bulkProgress()!.item }}</p>
    </div>
    }

    <!-- ========================================================================= -->
    <!-- TAB 1: PLATES LIST VIEW                                                  -->
    <!-- ========================================================================= -->
    @if (viewMode() === 'list') {
    <div class="space-y-4">

        <!-- Search and Actions Bar -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="relative flex-1 min-w-[200px] max-w-sm">
                <svg class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input type="text" [value]="searchQuery()" (input)="searchQuery.set($any($event.target).value)"
                    placeholder="Search license plates..."
                    class="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-8 py-2 text-xs text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition" />
                @if (searchQuery()) {
                <button type="button" (click)="searchQuery.set('')" class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                }
            </div>

            <div class="flex items-center gap-2">
                <button type="button" (click)="setViewMode('bulk-add')"
                    class="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition inline-flex items-center gap-1.5 shadow-sm">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add / Bulk Import
                </button>
            </div>
        </div>

        <!-- Multi-select Action Bar -->
        @if (selectedCount() > 0) {
        <div class="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
            <div class="flex items-center gap-2">
                <span class="bg-blue-600 text-white font-bold text-xs px-2 py-0.5 rounded-full">{{ selectedCount() }}</span>
                <span class="text-xs font-medium text-blue-900">plates selected</span>
            </div>

            <div class="flex items-center gap-2">
                @if (bulkRemoveConfirm()) {
                <div class="inline-flex items-center gap-2">
                    <span class="text-xs text-red-600 font-semibold">Remove {{ selectedCount() }} plate(s)?</span>
                    <button type="button" (click)="executeBulkRemove()" [disabled]="bulkProcessing()"
                        class="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2.5 py-1 rounded-md transition">
                        Yes, Remove
                    </button>
                    <button type="button" (click)="bulkRemoveConfirm.set(false)"
                        class="bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-medium px-2 py-1 rounded-md transition">
                        Cancel
                    </button>
                </div>
                } @else {
                <button type="button" (click)="bulkRemoveConfirm.set(true)" [disabled]="bulkProcessing()"
                    class="bg-white hover:bg-red-50 text-red-600 border border-red-200 text-xs font-semibold px-3 py-1.5 rounded-lg transition inline-flex items-center gap-1.5 shadow-sm">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Remove Selected
                </button>
                <button type="button" (click)="clearSelection()"
                    class="text-xs text-slate-500 hover:text-slate-800 font-medium px-2 py-1 transition">
                    Clear
                </button>
                }
            </div>
        </div>
        }

        <!-- Plates List Table / Cards -->
        @if (loading()) {
        <div class="flex justify-center items-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-blue-600"></div>
        </div>
        } @else if (plates().length > 0) {
        <div class="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <!-- Table Header -->
            <div class="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <div class="flex items-center gap-3">
                    <label class="inline-flex items-center cursor-pointer">
                        <input type="checkbox" [checked]="isAllSelected()" (change)="toggleSelectAll()"
                            class="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                    </label>
                    <span>License Plate</span>
                </div>
                <span>Actions</span>
            </div>

            <!-- Scrollable Items List -->
            <div class="max-h-[380px] overflow-y-auto divide-y divide-slate-100">
                @for (plate of filteredPlates(); track plate.id) {
                @let isSelected = selectedPlateIds().has(plate.id ?? '');
                <div [class]="isSelected ? 'bg-blue-50/40' : 'hover:bg-slate-50/60'"
                    class="flex items-center justify-between gap-3 px-4 py-2.5 transition-colors">
                    <!-- Plate Info & Checkbox -->
                    <div class="flex items-center gap-3 min-w-0">
                        <input type="checkbox" [checked]="isSelected" (change)="toggleSelectPlate(plate.id!)"
                            class="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                        <div class="flex items-center gap-2 flex-wrap">
                            <span class="bg-slate-900 text-white font-mono text-sm px-2.5 py-1 rounded-md tracking-wider shadow-sm font-semibold">
                                {{ plate.fullNumber }}
                            </span>
                            @if (plate.countryCode) {
                            <span class="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-0.5 rounded border border-slate-200">
                                {{ plate.countryCode }}
                            </span>
                            }
                            @if (plate.type && plate.type !== 'Personal') {
                            <span class="bg-slate-100 text-slate-500 text-[11px] px-1.5 py-0.5 rounded">
                                {{ plate.type }}
                            </span>
                            }
                        </div>
                    </div>

                    <!-- Row Actions -->
                    <div class="flex items-center gap-2 shrink-0">
                        <!-- Copy button -->
                        <button type="button" (click)="copyToClipboard(plate.fullNumber ?? '', plate.id!)"
                            class="text-slate-400 hover:text-slate-600 p-1.5 rounded-md hover:bg-slate-100 transition-colors"
                            [title]="copiedId() === plate.id ? 'Copied!' : 'Copy license plate'">
                            @if (copiedId() === plate.id) {
                            <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                            } @else {
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                            </svg>
                            }
                        </button>

                        <!-- Remove button / confirmation -->
                        @if (removeConfirmId() === plate.id) {
                        <div class="inline-flex items-center gap-1.5">
                            <span class="text-xs text-red-600 font-semibold">Remove?</span>
                            <button type="button" (click)="removePlate(plate)" [disabled]="removingId() === plate.id"
                                class="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2 py-0.5 rounded transition">
                                {{ removingId() === plate.id ? '...' : 'Yes' }}
                            </button>
                            <button type="button" (click)="cancelRemove()"
                                class="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs px-2 py-0.5 rounded transition">
                                No
                            </button>
                        </div>
                        } @else {
                        <button type="button" (click)="confirmRemove(plate.id!)"
                            class="text-slate-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                            title="Remove plate">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                        }
                    </div>
                </div>
                }
            </div>

            @if (filteredPlates().length === 0 && searchQuery()) {
            <div class="py-8 text-center text-slate-400 text-xs italic">
                No license plates match "{{ searchQuery() }}"
            </div>
            }
        </div>
        } @else {
        <div class="text-center py-12 border border-dashed border-slate-200 rounded-xl p-6 bg-slate-50/50">
            <div class="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1" />
                </svg>
            </div>
            <h3 class="text-sm font-semibold text-slate-900 mb-1">No License Plates</h3>
            <p class="text-xs text-slate-500 mb-4 max-w-sm mx-auto">This member does not have any active license plates assigned.</p>
            <div class="flex items-center justify-center gap-2">
                <button type="button" (click)="setViewMode('bulk-add')"
                    class="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition shadow-sm inline-flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Bulk Import Plates
                </button>
                <button type="button" (click)="setViewMode('single-add')"
                    class="bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-semibold px-3.5 py-2 rounded-lg transition">
                    + Add Single Plate
                </button>
            </div>
        </div>
        }
    </div>
    }

    <!-- ========================================================================= -->
    <!-- TAB 2: BULK ADD / QUICK IMPORT VIEW                                      -->
    <!-- ========================================================================= -->
    @if (viewMode() === 'bulk-add') {
    <div class="rounded-xl border border-slate-200 bg-slate-50/70 p-5 space-y-4">
        <div>
            <h3 class="text-sm font-bold text-slate-900">Bulk Import License Plates</h3>
            <p class="text-xs text-slate-500 mt-0.5">Paste or type multiple license plates from spreadsheets, documents, or lists. Separate items by new lines, commas, or spaces.</p>
        </div>

        <!-- Configuration row -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label for="bulkCountry" class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Default Country</label>
                <select id="bulkCountry" [value]="bulkCountry()" (change)="bulkCountry.set($any($event.target).value)"
                    class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition">
                    @for (c of countries; track c.code) {
                    <option [value]="c.code">{{ c.code }}</option>
                    }
                </select>
            </div>
            <div>
                <label for="bulkType" class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Plate Type</label>
                <select id="bulkType" [value]="bulkType()" (change)="bulkType.set($any($event.target).value)"
                    class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition">
                    @for (t of plateTypes; track t.value) {
                    <option [value]="t.value">{{ t.label }}</option>
                    }
                </select>
            </div>
        </div>

        <!-- Textarea input -->
        <div>
            <label for="bulkInputArea" class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">
                License Plates List
            </label>
            <textarea id="bulkInputArea" rows="5" [value]="bulkInput()" (input)="bulkInput.set($any($event.target).value)"
                placeholder="e.g.&#10;AM646FI&#10;EF673BN, FR37GB&#10;FR498BW L104KH"
                class="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 font-mono text-xs text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition resize-y"></textarea>
        </div>

        <!-- Live Parsed Preview -->
        @if (parsedBulkPlates().length > 0) {
        <div class="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <div class="flex items-center justify-between flex-wrap gap-2 text-xs">
                <span class="font-bold text-slate-900">
                    Parsed Summary:
                    <span class="text-blue-600">{{ validBulkPlatesToAdd().length }} valid new plate(s)</span>
                </span>
                <div class="flex items-center gap-2 text-[11px] text-slate-500">
                    @if (duplicateInInputCount() > 0) {
                    <span class="text-amber-600 font-medium">⚠️ {{ duplicateInInputCount() }} duplicate(s) in input ignored</span>
                    }
                    @if (alreadyAssignedCount() > 0) {
                    <span class="text-slate-500">ℹ️ {{ alreadyAssignedCount() }} already assigned</span>
                    }
                    @if (invalidBulkCount() > 0) {
                    <span class="text-red-600 font-medium">❌ {{ invalidBulkCount() }} invalid</span>
                    }
                </div>
            </div>

            <!-- Chips container -->
            <div class="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pt-1">
                @for (item of validBulkPlatesToAdd(); track item.normalized) {
                <span class="inline-flex items-center gap-1.5 bg-slate-900 text-white font-mono text-xs px-2.5 py-1 rounded-md tracking-wider shadow-sm">
                    {{ item.normalized }}
                    <button type="button" (click)="removeParsedChip(item.normalized)" class="text-slate-400 hover:text-white transition-colors">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </span>
                }
            </div>
        </div>
        }

        <!-- Itemized Batch Results (if any) -->
        @if (bulkAddResults()) {
        <div class="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <h4 class="text-xs font-bold text-slate-900 uppercase">Batch Execution Report</h4>
            <div class="max-h-40 overflow-y-auto space-y-1 text-xs">
                @for (r of bulkAddResults()!; track r.plate) {
                <div class="flex items-center justify-between gap-2 py-1 px-2 rounded" [class]="r.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'">
                    <span class="font-mono font-semibold">{{ r.plate }}</span>
                    <span>{{ r.success ? 'Added successfully' : r.error }}</span>
                </div>
                }
            </div>
        </div>
        }

        <!-- Actions -->
        <div class="flex items-center gap-3 pt-2">
            <button type="button" (click)="executeBulkAdd()"
                [disabled]="bulkProcessing() || validBulkPlatesToAdd().length === 0"
                class="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition shadow-sm inline-flex items-center gap-2">
                @if (bulkProcessing()) {
                <span class="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></span>
                Adding Plates...
                } @else {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                Add {{ validBulkPlatesToAdd().length }} Plate(s)
                }
            </button>
            <button type="button" (click)="setViewMode('list')" [disabled]="bulkProcessing()"
                class="bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-semibold px-4 py-2.5 rounded-lg transition">
                Back to List
            </button>
        </div>
    </div>
    }

    <!-- ========================================================================= -->
    <!-- TAB 3: SINGLE ADD FORM VIEW                                              -->
    <!-- ========================================================================= -->
    @if (viewMode() === 'single-add') {
    <form class="rounded-xl border border-slate-200 bg-slate-50/70 p-5 space-y-4 mb-5" [formGroup]="addForm"
        (ngSubmit)="addPlate()">

        <div>
            <h3 class="text-sm font-bold text-slate-900">Add Single License Plate</h3>
            <p class="text-xs text-slate-500 mt-0.5">Enter a single license plate with live EU plate preview and validation.</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label for="plateCountry" class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Country of registration</label>
                <select id="plateCountry" formControlName="country"
                    class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition">
                    @for (c of countries; track c.code) {
                    <option [value]="c.code">{{ c.code }}</option>
                    }
                </select>
            </div>
            <div>
                <label for="plateType" class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Plate type</label>
                <select id="plateType" formControlName="type"
                    class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition">
                    @for (t of plateTypes; track t.value) {
                    <option [value]="t.value">{{ t.label }}</option>
                    }
                </select>
            </div>
        </div>

        <div>
            <label for="plate" class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">License plate</label>
            <app-plate editable [euBand]="selectedCountry().euBand" [countryCode]="selectedCountryCode()">
                <input id="plate" type="text" formControlName="plate" (input)="onPlateFieldInput($event, 'plate')"
                    [maxlength]="maxPlateLength" autocomplete="off" spellcheck="false" placeholder="LL123AB" />
            </app-plate>
            @if (addForm.get('plate')?.touched && addForm.get('plate')?.invalid) {
            <span class="mt-1 block text-xs text-red-600">Please enter a valid license plate.</span>
            }
        </div>

        <!-- Repeat-to-confirm -->
        <div>
            <label for="plateRepeat" class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Repeat license plate</label>
            <app-plate editable [euBand]="selectedCountry().euBand" [countryCode]="selectedCountryCode()">
                <input id="plateRepeat" type="text" formControlName="plateRepeat"
                    (input)="onPlateFieldInput($event, 'plateRepeat')" [maxlength]="maxPlateLength"
                    autocomplete="off" spellcheck="false" />
            </app-plate>
            @if (platesMismatch) {
            <span class="mt-1 block text-xs text-red-600">The license plates do not match.</span>
            }
        </div>

        <div>
            <span class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Preview</span>
            <app-plate size="lg" [number]="plateValue()" [euBand]="selectedCountry().euBand"
                [countryCode]="selectedCountryCode()" [region]="plateRegion()"
                [regionSeparator]="selectedCountry().regionSeparator" placeholder="· · ·" />
        </div>

        <div class="flex gap-3 pt-2">
            <jjames-button type="submit" variant="primary" (clicked)="addPlate()" [disabled]="submitting()">
                {{ submitting() ? 'Adding...' : 'Add Plate' }}
            </jjames-button>
            <jjames-button type="button" variant="secondary" (clicked)="cancelAdd()" [disabled]="submitting()">Cancel</jjames-button>
        </div>
    </form>
    }

    </div>

    <!-- Pinned Footer -->
    <div class="px-6 py-4 border-t border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/80">
        <div class="text-xs text-slate-500 font-medium">
            {{ plates().length }} active license plate{{ plates().length !== 1 ? 's' : '' }} assigned
        </div>
        <jjames-button variant="secondary" (clicked)="close()">Done</jjames-button>
    </div>
</div>

